import { LabelService } from '../../services/DataService';
import { Label, Contact } from '../../types/contacts';
import { callAdminApi } from './wixAdminApi';
import { getFirestore, getDocs, setDoc, deleteDoc, collection, doc } from 'firebase/firestore';
import { getFirebaseApp } from '../firebaseApp';

import { createApiError, ApiErrorCode} from '../../types/errors';

// Firestore `labels` collection (in the 'contacts' database) is the source of
// truth for label name + colour. Docs are keyed by the label's `key`.
const LABELS_DB = 'contacts';
const LABELS_COLLECTION = 'labels';

export class LabelsApi implements LabelService {
  private labelsCollection() {
    return collection(getFirestore(getFirebaseApp(), LABELS_DB), LABELS_COLLECTION);
  }

  /** Write/merge a label's name + colour into its Firestore doc. */
  private async writeLabelDoc(
    key: string,
    fields: { id?: string; name?: string; color?: string },
  ): Promise<void> {
    await setDoc(
      doc(this.labelsCollection(), key),
      {
        id: fields.id ?? key,
        key,
        ...(fields.name !== undefined ? { name: fields.name } : {}),
        ...(fields.color !== undefined ? { color: fields.color } : {}),
        updatedAt: Date.now(),
      },
      { merge: true },
    );
  }

  async getAllLabels(): Promise<Label[]> {
    try {
      const db = getFirestore(getFirebaseApp(), 'contacts');
      const snap = await getDocs(collection(db, 'labels'));
      if (!snap.empty) {
        return snap.docs.map(d => {
          const data = d.data();
          return {
            id: data.id || d.id,
            _id: data.id || d.id,
            key: data.key || d.id,
            name: data.name || '',
            displayName: data.name || '',
            color: data.color || '#3880ff',
          } as Label;
        });
      }
    } catch { /* fall through */ }

    // Firestore empty (before any label has been written) — fall back to Wix's
    // label list. Colour defaults until the label is edited once.
    try {
      const response = await callAdminApi('queryLabels', 'labels', { limit: 100 });
      if (!response._items) {
        throw createApiError(ApiErrorCode.DATA_NOT_FOUND, 'No labels found in response');
      }
      return response._items.map((item: unknown) => {
        const label = item as Partial<Label>;
        const displayName = label.name || label.displayName || '';
        return {
          id: label._id || label.id || label.key || '',
          name: displayName,
          color: label.color || '#3880ff',
          _id: label._id || '',
          key: label.key || ''
        };
      });
    } catch (error) {
      throw createApiError(ApiErrorCode.DATA_FETCH_FAILED ?? ApiErrorCode.UNKNOWN_ERROR, 'Error fetching labels', undefined, { originalError: error });
    }
  }

  async getContactLabels(contactId: string): Promise<Label[]> {
    if (!contactId) {
      throw createApiError(ApiErrorCode.CONTACT_INVALID_DATA, 'Contact ID is missing');
    }
    try {
      // 1. Fetch the contact
      const contact = await callAdminApi('getContact', 'contacts', { id: contactId });
      // 2. Extract label IDs from contact
      let labelIds: string[] = [];
      if (Array.isArray(contact.labelKeys)) {
        labelIds = [...labelIds, ...contact.labelKeys.map(String)];
      }
      if (contact.info && typeof contact.info === 'object' && contact.info !== null && 'labelKeys' in contact.info) {
        if (Array.isArray(contact.info.labelKeys)) {
          labelIds = [...labelIds, ...contact.info.labelKeys.map(String)];
        } else if (typeof contact.info.labelKeys === 'object' && contact.info.labelKeys !== null) {
          if ('items' in contact.info.labelKeys && Array.isArray(contact.info.labelKeys.items)) {
            labelIds = [...labelIds, ...contact.info.labelKeys.items.map(String)];
          } else {
            const keys = Object.keys(contact.info.labelKeys)
              .filter(key => key !== 'items' && key !== 'length');
            labelIds = [...labelIds, ...keys];
          }
        }
      }
      // Special case handling for 'custom.airbnb-client' format
      const potentialLabels = ['custom.airbnb-client', 'custom.residential-client', 'custom.commercial-client'];
      for (const labelId of potentialLabels) {
        if (!labelIds.includes(labelId) &&
          (contact.info && typeof contact.info === 'object' && 'labelKeys' in contact.info && 
           contact.info.labelKeys && contact.info.labelKeys[labelId as keyof typeof contact.info.labelKeys] ||
            (contact.info && typeof contact.info === 'object' && 
             'customFields' in contact.info && 
             contact.info.customFields && 
             typeof contact.info.customFields === 'object' &&
             'labels' in contact.info.customFields && 
             Array.isArray(contact.info.customFields.labels) &&
             contact.info.customFields.labels.includes(labelId)))) {
          labelIds.push(labelId);
        }
      }
      // Remove duplicates
      labelIds = Array.from(new Set(labelIds));
      if (labelIds.length === 0) {
        throw createApiError(ApiErrorCode.CONTACT_NOT_FOUND, 'No label IDs found for contact');
      }
      // 3. Fetch only the label objects for these IDs (try key, fallback to _id if needed)
      // Assume most label IDs are keys (e.g., custom.active-clients)
      let response;
      try {
        response = await callAdminApi('queryLabels', 'labels', {
          filter: { key: { $in: labelIds } },
          limit: labelIds.length
        });
      } catch {
        // Fallback: try _id if key fails
        try {
          response = await callAdminApi('queryLabels', 'labels', {
            filter: { _id: { $in: labelIds } },
            limit: labelIds.length
          });
        } catch (error) {
          throw createApiError(ApiErrorCode.DATA_NOT_FOUND, 'Error fetching labels by key and _id', undefined, { originalError: error });
        }
      }
      // 4. Get label colours from the Firestore-backed label list (source of truth).
      const colorByName = new Map<string, string>();
      try {
        for (const l of await this.getAllLabels()) colorByName.set(l.name, l.color);
      } catch { /* colours default below */ }

      // 5. Map and return
      if (!response._items) {
        throw createApiError(ApiErrorCode.DATA_NOT_FOUND, 'No label items found in response');
      }
      return response._items.map((item: unknown) => {
        const label = item as Partial<Label>;
        const displayName = label.name || label.displayName || '';
        return {
          id: label._id || label.id || label.key || '',
          name: displayName,
          color: colorByName.get(displayName) || label.color || '#3880ff',
          _id: label._id || '',
          key: label.key || ''
        };
      });
    } catch (error) {
      createApiError(ApiErrorCode.LABEL_CONTACT_LABELS_FETCH_FAILED ?? ApiErrorCode.CONTACT_FETCH_FAILED, 'Error getting contact labels', undefined, { originalError: error });
      throw createApiError(ApiErrorCode.CONTACT_FETCH_FAILED, 'Error getting contact labels', undefined, { originalError: error });
    }
  }

  async assignLabelsToContact(contactId: string, labelIds: string[]): Promise<boolean>;
  async assignLabelsToContact(params: unknown): Promise<boolean>;
  async assignLabelsToContact(contactIdOrParams: string | unknown, labelIdsParam?: string[]): Promise<boolean> {
    try {
      let contactId: string, labelIds: string[];
      
      // Handle both interface formats
      if (typeof contactIdOrParams === 'string' && labelIdsParam !== undefined) {
        // Interface format: assignLabelsToContact(contactId: string, labelIds: string[])
        contactId = contactIdOrParams;
        labelIds = labelIdsParam;
      } else {
        // Legacy format: assignLabelsToContact(params: unknown)
        const params = contactIdOrParams;
        
        // Handle array format: [contactId, labelIds] 
        if (Array.isArray(params)) {
          contactId = params[0] as string;
          labelIds = params[1] as string[];
        } else {
          // Handle object format: {contactId, labelIds} or {id, labelKeys}
          const paramsObj = params as Record<string, unknown>;
          contactId = (paramsObj.contactId || paramsObj.id) as string;
          labelIds = (paramsObj.labelIds || paramsObj.labelKeys) as string[];
        }
      }

      // Validate required parameters
      if (!contactId) {
        throw createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'contactId is required for label assignment'
        );
      }

      if (!labelIds) {
        throw createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'labelIds array is required for label assignment'
        );
      }

      // If we have no labels to assign and the API doesn't allow empty arrays,
      // we need to use a different approach
      if (labelIds.length === 0) {
        // Get current labels
        const currentLabels = await this.getContactLabels(contactId);

        if (currentLabels.length === 0) {
          return true;
        }

        // Use a dummy label that we'll remove later
        const dummyLabelId = 'system.dummy-temp-label';

        try {
          // First assign a dummy label (this will replace all existing labels)
          await callAdminApi('labelContact', 'contacts', {
            contactId: contactId,
            labelKeys: [dummyLabelId]
          });

          // Then instead of trying to remove the dummy label, just set an empty array
          try {
            await callAdminApi('labelContact', 'contacts', {
              contactId: contactId,
              labelKeys: []
            });
          } catch (emptyLabelError) {
            // If setting empty labels fails, that's okay - the dummy label is harmless
            console.warn('Could not set empty labels, dummy label may remain:', emptyLabelError);
          }

          return true;
        } catch (error) {
          throw createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error in empty label assignment process', undefined, { originalError: error });
        }
      }

      // For normal case with labels to assign
      try {
        // This will replace all existing labels with the new ones
        await callAdminApi('labelContact', 'contacts', {
          contactId: contactId,
          labelKeys: labelIds
        });

        // Verify the labels were actually set
        const updatedLabels = await this.getContactLabels(contactId);
        const updatedLabelIds = updatedLabels.map(label => label.id);

        // Check if all requested labels are now assigned
        const allLabelsAssigned = labelIds.every((id: string) =>
          updatedLabelIds.includes(id) ||
          // Also check if the label exists with a different format (e.g., with/without namespace)
          updatedLabelIds.some(updatedId =>
            updatedId.endsWith(id) || id.endsWith(updatedId)
          )
        );

        if (!allLabelsAssigned) {
          console.warn('Not all requested labels were assigned. Requested:', labelIds, 'Actual:', updatedLabelIds);
        }

        return true;
      } catch (error) {
        // Use API error util for main assignment failure
        createApiError(ApiErrorCode.LABEL_ASSIGN_FAILED ?? ApiErrorCode.CONTACT_UPDATE_FAILED, `Error assigning labels to contact ${contactId}`, undefined, { originalError: error });

        // If the first attempt fails, try with a single label at a time
        let successCount = 0;

        for (const labelId of labelIds) {
          try {
            await callAdminApi('labelContact', 'contacts', {
              contactId: contactId,
              labelKeys: [labelId]
            });
            successCount++;
          } catch (labelError) {
            // Use API error util for individual label assignment failure
            createApiError(ApiErrorCode.LABEL_ASSIGN_SINGLE_FAILED ?? ApiErrorCode.CONTACT_UPDATE_FAILED, `Error assigning individual label ${labelId}`, undefined, { originalError: labelError });
          }
        }

        return successCount > 0;
      }
    } catch (error) {
      throw createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error in label assignment process', undefined, { originalError: error });
    }
  }

  async removeLabelsFromContact(contactId: string, labelIds: string[]): Promise<boolean>;
  async removeLabelsFromContact(params: unknown): Promise<boolean>;
  async removeLabelsFromContact(contactIdOrParams: string | unknown, labelIdsParam?: string[]): Promise<boolean> {
    try {
      let contactId: string, labelIds: string[];
      
      // Handle both interface formats
      if (typeof contactIdOrParams === 'string' && labelIdsParam !== undefined) {
        // Interface format: removeLabelsFromContact(contactId: string, labelIds: string[])
        contactId = contactIdOrParams;
        labelIds = labelIdsParam;
      } else {
        // Legacy format: removeLabelsFromContact(params: unknown)
        const params = contactIdOrParams;
        
        // Handle array format: [contactId, labelIds] 
        if (Array.isArray(params)) {
          contactId = params[0] as string;
          labelIds = params[1] as string[];
        } else {
          // Handle object format: {contactId, labelIds} or {id, labelKeys}
          const paramsObj = params as Record<string, unknown>;
          contactId = (paramsObj.contactId || paramsObj.id) as string;
          labelIds = (paramsObj.labelIds || paramsObj.labelKeys) as string[];
        }
      }

      // Validate required parameters
      if (!contactId) {
        throw createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'contactId is required for label removal'
        );
      }

      if (!labelIds || labelIds.length === 0) {
        throw createApiError(
          ApiErrorCode.CONTACT_UPDATE_FAILED,
          'labelIds array is required and cannot be empty for label removal'
        );
      }

      // Use callAdminApi for all label operations
      try {
        await callAdminApi('unlabelContact', 'contacts', {
          contactId: contactId,
          labelKeys: labelIds
        });
        return true;
      } catch (error) {
        throw createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error using unlabelContact method', undefined, { originalError: error });
      }
    } catch (error) {
      throw createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error in removeLabelsFromContact process', undefined, { originalError: error });
    }
  }

  async createLabel(name: string, color?: string): Promise<Label> {
    try {
      console.log('createLabel called with name:', name, 'color:', color);
      
      // Validate input parameters
      if (!name || typeof name !== 'string' || !name.trim()) {
        throw createApiError(ApiErrorCode.DATA_INVALID_FORMAT, 'Label name is required and must be a non-empty string', undefined, { name });
      }
      
      // Use the correct parameter format - pass displayName as a property
      const result = await callAdminApi('findOrCreateLabel', 'labels', {
        displayName: name.trim()
      });

      console.log('createLabel API result:', result);

      // Check if we got a label directly or need to extract it
      const labelData = result.label || result;
      console.log('Extracted label data:', labelData);

      // Extract the label from the result
      const createdLabel = labelData as { 
        _id?: string; 
        id?: string; 
        key?: string; 
        displayName?: string;
        namespace?: string;
      };

      const resolvedId = createdLabel._id || createdLabel.id || createdLabel.key || '';
      const resolvedKey = createdLabel.key || resolvedId;
      const finalColor = color || '#3880ff';

      // Write the Firestore doc — the source of truth for name + colour. The Wix
      // label already exists at this point, so a failure here is non-fatal: the
      // label shows with the default colour until it's edited.
      try {
        await this.writeLabelDoc(resolvedKey, { id: resolvedId, name, color: finalColor });
      } catch (fsError) {
        console.error('[labels] Failed to write label colour doc after creation:', fsError);
      }

      // Return the label in our standard format
      return {
        id: resolvedId,
        name: createdLabel.displayName || name,
        color: finalColor,
        _id: createdLabel._id || '',
        key: resolvedKey
      };
    } catch (error: unknown) {
      throw createApiError(ApiErrorCode.CONTACT_CREATION_FAILED, 'Error creating label', undefined, { originalError: error });
    }
  }

  async updateLabel(id: string, name: string, color?: string): Promise<Label> {
    try {
      // Get the existing label
      const allLabels = await this.getAllLabels();
      const existingLabel = allLabels.find(label => 
        label.id === id || label.key === id || label._id === id
      );

      if (!existingLabel) {
        throw new Error(`Label with ID ${id} not found`);
      }

      // ✅ Use renameLabel to update the display name
      try {
        await callAdminApi('renameLabel', 'labels', {
          key: existingLabel.key || existingLabel.id,
          options: { displayName: name }
        });
      } catch (renameError) {
        throw renameError;
      }

      // ✅ Persist name + colour to the Firestore doc (source of truth). Always
      //    runs — a rename with no colour still needs the doc's `name` updated.
      const effectiveColor = color || existingLabel.color || '#3880ff';
      await this.writeLabelDoc(existingLabel.key || existingLabel.id, {
        id: existingLabel.id,
        name,
        color: effectiveColor,
      });

      // ✅ Return the updated label
      const updatedLabel = {
        id: existingLabel.id,
        name: name,
        color: effectiveColor,
        _id: existingLabel._id,
        key: existingLabel.key
      };

      return updatedLabel;

    } catch (error: unknown) {
      throw createApiError(ApiErrorCode.CONTACT_UPDATE_FAILED, 'Error updating label', undefined, { originalError: error });
    }
  }

  async getLabelColor(labelName: string): Promise<string | null> {
    try {
      const labels = await this.getAllLabels();
      const match = labels.find(l => l.name === labelName);
      return match?.color ?? null;
    } catch (error) {
      throw createApiError(ApiErrorCode.CONTACT_FETCH_FAILED, 'Error retrieving label color', undefined, { originalError: error });
    }
  }

  async deleteLabel(id: string): Promise<boolean> {
    try {
      // Resolve the label's key (Firestore doc id + Wix labelId).
      let labelKey = id;
      try {
        const allLabels = await this.getAllLabels();
        const match = allLabels.find(label =>
          label.id === id || label.key === id || label._id === id
        );
        if (match) labelKey = match.key || match.id;
      } catch (error) {
        console.warn('Error finding label for deletion:', error);
      }

      // Delete from Wix.
      await callAdminApi('deleteLabel', 'labels', { labelId: labelKey });

      // Delete the Firestore doc (source of truth). Non-fatal — the Wix label
      // is already gone, so a stale colour doc is harmless.
      try {
        await deleteDoc(doc(this.labelsCollection(), labelKey));
      } catch (fsError) {
        console.error('[labels] Failed to remove label colour doc on delete:', fsError);
      }

      return true;
    } catch (error: unknown) {
      throw createApiError(ApiErrorCode.CONTACT_DELETION_FAILED, 'Error deleting label', undefined, { originalError: error });
    }
  }

  async getContactsByLabel(labelNameOrId: string): Promise<Contact[]> {
    try {
      let labelId = labelNameOrId;
      let labelName = labelNameOrId;
      // Smart detection: determine if input is a name or ID
      const isId = labelNameOrId.includes('.') || labelNameOrId.match(/^[a-f0-9-]+$/);
      if (isId) {
        // Input is an ID, find the corresponding name
        const allLabels = await this.getAllLabels();
        const foundLabel = allLabels.find(label => 
          label.id === labelNameOrId || 
          label.key === labelNameOrId || 
          label._id === labelNameOrId
        );
        if (foundLabel) {
          labelName = foundLabel.name;
          labelId = foundLabel.id;
        } else {
          return [];
        }
      } else {
        // Input is a name, find the corresponding ID
        const allLabels = await this.getAllLabels();
        const foundLabel = allLabels.find(label => 
          label.name.toLowerCase() === labelNameOrId.toLowerCase()
        );
        if (foundLabel) {
          labelId = foundLabel.id;
          labelName = foundLabel.name;
        } else {
          return [];
        }
      }
      // Use the resolved ID for filtering (Wix contacts store IDs)
      const response = await callAdminApi('queryContacts', 'contacts', {
        filter: {
          'info.labelKeys.items': { $in: [labelId] }
        },
        fields: ['info.labelKeys', 'info.name', 'info.emails', 'info.phones','info.addresses']
      });
      if (!response || !response.items || response.items.length === 0) {
        return [];
      }
      return response.items.map((contact: unknown) => {
        const contactObj = contact as Record<string, unknown>;
        const info = contactObj.info as Record<string, unknown> || {};
        const name = info.name as Record<string, unknown> || {};
        const addresses = Array.isArray(info.addresses) ? info.addresses : [];
        let labelIds: string[] = [];
        if (
          info.labelKeys &&
          typeof info.labelKeys === 'object' &&
          info.labelKeys !== null &&
          Array.isArray((info.labelKeys as { items?: unknown }).items)
        ) {
          labelIds = (info.labelKeys as { items: string[] }).items;
        }
        const emails = Array.isArray(info.emails) ? info.emails : [];
        const phones = Array.isArray(info.phones) ? info.phones : [];
        // Extract address and addressLine1 if available
        let address = '';
        let addressLine1 = '';
        if (addresses.length > 0 && typeof addresses[0] === 'object' && addresses[0] !== null) {
          const addrObj = addresses[0] as Record<string, unknown>;
          address = String(addrObj.formatted || addrObj.address || '') || '';
          addressLine1 = String(addrObj.addressLine1 || addrObj.line1 || '') || '';
        }
        // Create a ContactItem with all required properties
        return {
          id: String(contactObj._id || ''),
          _id: String(contactObj._id || ''),
          firstName: String(name.first || ''),
          lastName: String(name.last || ''),
          company: info.company as string || '',
          jobTitle: info.jobTitle as string || '',
          avatar: typeof contactObj.picture === 'string' ? contactObj.picture : '',
          source: 'wix',
          createdAt: contactObj._createdDate as string || '',
          updatedAt: contactObj._updatedDate as string || '',
          emails: emails,
          phones: phones,
          addresses: addresses,
          address: address,
          addressLine1: addressLine1,
          customFields: Array.isArray(info.customFields) ? info.customFields : [],
          labels: labelIds,
          labelName: labelName
        };
      });
    } catch (error) {
      createApiError(ApiErrorCode.LABEL_CONTACTS_BY_LABEL_FETCH_FAILED ?? ApiErrorCode.DATA_FETCH_FAILED, `Error fetching contacts by label ${labelNameOrId}`, undefined, { originalError: error });
      return [];
    }
  }

  async searchContactsByLabel(labelId: string): Promise<Label[]> {
    try {
      // First, get the label information
      let labelInfo: Label | null = null;
      try {
        const allLabels = await this.getAllLabels();
        labelInfo = allLabels.find(label => label.id === labelId) || null;
        if (!labelInfo) {
          console.warn(`Label with ID ${labelId} not found in getAllLabels`);
          // Create a basic label object if we can't find it
          labelInfo = {
            id: labelId,
            name: labelId.split('.').pop() || labelId,
            color: '#3880ff'
          };
        }
      } catch (labelError) {
        // Use API error util for label info fetch failure
        createApiError(ApiErrorCode.LABEL_INFO_FETCH_FAILED ?? ApiErrorCode.DATA_FETCH_FAILED, 'Error getting label info during searchContactsByLabel', undefined, { originalError: labelError });
      }
      // Build search query to count contacts with this label via Firebase function
      const response = await callAdminApi('queryContacts', 'contacts', {
        filter: {
          'info.labelKeys.items': { $in: [labelId] }
        },
        limit: 0 // We just need the count
      });
      const contactCount = response.totalResults || 0;
      // Return the label with count information
      if (labelInfo) {
        return [{
          ...labelInfo,
          contactCount: contactCount as number
        }];
      }
      // Return a basic label if we couldn't find it
      return [{
        id: labelId,
        name: labelId.split('.').pop() || labelId,
        color: '#3880ff',
        contactCount: contactCount as number
      }];
    } catch (error) {
      createApiError(ApiErrorCode.LABEL_SEARCH_BY_LABEL_FAILED ?? ApiErrorCode.DATA_FETCH_FAILED, 'Error searching contacts by label', undefined, { originalError: error });
      return [];
    }
  }
  
  async getContactCountsByLabel(): Promise<Record<string, number>> {
    const labelCounts: Record<string, number> = {};
    try {
      // Get all available labels first
      const allLabels = await this.getAllLabels();
      for (const label of allLabels) {
        try {
          // Use the correct path for labels in the query via Firebase function
          const response = await callAdminApi('queryContacts', 'contacts', {
            filter: {
              'info.labelKeys.items': { $in: [label.id] }
            },
            limit: 0
          });
          // Ensure totalResults is always a number
          const totalResults = typeof response.totalResults === 'number' 
            ? response.totalResults 
            : parseInt(String(response.totalResults), 10) || 0;
          // Store the count for this label
          labelCounts[label.id] = totalResults;
        } catch (error) {
          // Use API error util for contact count failure
          createApiError(ApiErrorCode.LABEL_CONTACT_COUNT_FAILED ?? ApiErrorCode.DATA_FETCH_FAILED, `Error counting contacts for label ${label.id}`, undefined, { originalError: error });
          labelCounts[label.id] = 0;
        }
      }
      return labelCounts;
    } catch (error) {
      // Use API error util for overall contact count fetch failure
      createApiError(ApiErrorCode.LABEL_CONTACT_COUNTS_FETCH_FAILED ?? ApiErrorCode.DATA_FETCH_FAILED, 'Error getting contact counts by label', undefined, { originalError: error });
      return {};
    }
  }
}

